import {
  addItemToOrder,
  removeItem as apiRemoveItem,
  updatePickupLocation,
  updateItemQuantity,
} from '@/services/foodAndDrinks/editOrder'
import {
  mockPickupEnabled,
  mockPickupPatch,
} from '@/services/foodAndDrinks/mockPickup'
import { postOrder } from '@/services/foodAndDrinks/postOrder'
import { FnbOrderResponse, MenuItem } from '@/types/fnb'
import { create } from 'zustand'

interface FnbState {
  basket: Record<string, MenuItem>
  validatedOrderUid: string | null
  itemLoading: boolean
  isCreatingOrder: boolean
  cartError: string | null
  cartOpen: boolean
  createdOrder: FnbOrderResponse | null
  // Pickup location `ref` chosen for `options` mode. Sent at creation
  // (postOrder) and changeable until payment via `changePickupLocation`; null
  // means "no explicit pick", and the backend applies the venue default.
  pickupLocationId: string | null
  pickupLocationError: string | null
  pickupLocationLoading: boolean
  setPickupLocationId: (ref: string | null) => void
  changePickupLocation: (ref: string) => Promise<boolean>
  setCartOpen: (cartOpen: boolean) => void
  setCreatedOrder: (order: FnbOrderResponse | null) => void
  addItem: (
    instanceUid: string,
    item: MenuItem,
    quantity: number,
  ) => Promise<boolean>
  removeItem: (product: MenuItem) => Promise<void>
  decreaseItem: (product: MenuItem, quantity: number) => Promise<void>
  clearBasket: () => void
}

export const useFnbStore = create<FnbState>((set, get) => ({
  basket: {},
  validatedOrderUid: null,
  createdOrder: null,
  cartError: null,
  cartOpen: false,
  itemLoading: false,
  isCreatingOrder: false,
  pickupLocationId: null,
  pickupLocationError: null,
  pickupLocationLoading: false,

  setPickupLocationId: (ref: string | null) => set({ pickupLocationId: ref }),
  setCartOpen: (cartOpen: boolean) => set({ cartOpen }),
  setCreatedOrder: (order: FnbOrderResponse | null) =>
    set({ createdOrder: order }),

  addItem: async (instanceUid: string, product: MenuItem, quantity: number) => {
    // Read synchronously — `disabled` lags a render, so fast repeat taps would
    // otherwise each fire a request. `false` tells callers they were a no-op.
    if (get().isCreatingOrder || get().itemLoading) return false

    set({ itemLoading: true, cartError: null })

    const { validatedOrderUid, basket, pickupLocationId } = get()
    const currentItem = basket[product.item_id]
    const prevBasket = { ...basket }

    set({
      basket: {
        ...basket,
        [product.item_id]: {
          ...basket[product.item_id],
          ...product,
          quantity: (currentItem?.quantity || 0) + quantity,
        },
      },
    })

    try {
      let response: FnbOrderResponse

      if (!validatedOrderUid) {
        set({ isCreatingOrder: true })
        response = await postOrder(
          instanceUid,
          [{ ...product, quantity }],
          pickupLocationId,
        )
        set({ validatedOrderUid: response.fnb_uid, isCreatingOrder: false })
      } else if (currentItem?.item_uid) {
        response = await updateItemQuantity(
          currentItem.item_uid,
          (currentItem.quantity || 0) + quantity,
        )
      } else {
        response = await addItemToOrder(validatedOrderUid, {
          item_id: product.item_id,
          name: product.name,
          quantity,
          price: Number(product.price),
          is_double_measure: false,
        })
      }

      const backendItem = response.items.find(
        (i) => i.item_id === product.item_id,
      )

      set((state) => ({
        createdOrder: response,
        basket: {
          ...state.basket,
          [product.item_id]: {
            ...state.basket[product.item_id],
            item_uid: backendItem?.uid,
            quantity:
              backendItem?.quantity ?? (currentItem?.quantity || 0) + quantity,
          },
        },
      }))

      return true
    } catch (err: any) {
      set({
        basket: prevBasket,
        cartError: err?.message || 'Failed to add item',
        isCreatingOrder: false,
      })

      return Promise.reject(err)
    } finally {
      set({ itemLoading: false })
    }
  },

  // Re-points an existing order. Before the order exists the ref is just local
  // state, so setPickupLocationId is enough and no request is needed.
  changePickupLocation: async (ref: string) => {
    const { validatedOrderUid, pickupLocationId } = get()

    if (pickupLocationId === ref) return true

    if (!validatedOrderUid) {
      set({ pickupLocationId: ref, pickupLocationError: null })
      return true
    }

    set({
      pickupLocationId: ref,
      pickupLocationLoading: true,
      pickupLocationError: null,
    })

    try {
      const response = mockPickupEnabled
        ? mockPickupPatch(get().createdOrder, ref)
        : await updatePickupLocation(validatedOrderUid, ref)
      set({ createdOrder: response })
      return true
    } catch (err: any) {
      set({
        pickupLocationId,
        pickupLocationError:
          err?.response?.data?.detail || 'Could not change pickup location',
      })
      return false
    } finally {
      set({ pickupLocationLoading: false })
    }
  },

  decreaseItem: async (product: MenuItem, quantity: number) => {
    if (get().itemLoading) return

    set({ itemLoading: true, cartError: null })

    const prevBasket = { ...get().basket }
    const currentItem = prevBasket[product.item_id]

    if (!currentItem?.item_uid) {
      set({ itemLoading: false })
      return
    }

    const newQuantity = (currentItem.quantity ?? 0) - quantity
    const shouldRemove = newQuantity <= 0
    const wasLastItem = Object.keys(prevBasket).length === 1

    set((state) => {
      if (shouldRemove) {
        const { [product.item_id]: _, ...rest } = state.basket
        return {
          basket: rest,
          ...(wasLastItem && { validatedOrderUid: null, createdOrder: null }),
        }
      }

      return {
        basket: {
          ...state.basket,
          [product.item_id]: {
            ...state.basket[product.item_id],
            quantity: newQuantity,
          },
        },
      }
    })

    try {
      const response = shouldRemove
        ? await apiRemoveItem(currentItem.item_uid)
        : await updateItemQuantity(currentItem.item_uid, newQuantity)

      set({ createdOrder: response })
    } catch (err: any) {
      set({
        basket: prevBasket,
        cartError: err?.message || 'Failed to update item',
      })

      return Promise.reject(err)
    } finally {
      set({ itemLoading: false })
    }
  },

  removeItem: async (product: MenuItem) => {
    if (get().itemLoading) return

    set({ itemLoading: true, cartError: null })

    const { basket } = get()
    const currentItem = basket[product.item_id]
    const prevBasket = { ...basket }
    const wasLastItem = Object.keys(prevBasket).length === 1

    if (!currentItem?.item_uid) {
      set({ itemLoading: false })
      return
    }

    const { [product.item_id]: _, ...rest } = basket
    set({
      basket: rest,
      ...(wasLastItem && { validatedOrderUid: null, createdOrder: null }),
    })

    try {
      const response = await apiRemoveItem(currentItem.item_uid)
      set({ createdOrder: response })
    } catch (err: any) {
      set({
        basket: prevBasket,
        cartError: err?.message || 'Failed to remove item',
      })

      return Promise.reject(err)
    } finally {
      set({ itemLoading: false })
    }
  },

  clearBasket: () => {
    set({
      basket: {},
      validatedOrderUid: null,
      createdOrder: null,
      pickupLocationId: null,
      pickupLocationError: null,
    })
  },
}))
